import { useState } from "react";
import { useTranslation } from "react-i18next";
import { LOGO_CIVISOFT, CIVISOFT_CONTACT, VERSIONS_GEO5 } from "../constants";
import { GEO5_MODULES_LISTE } from "../kb/geo5Modules";
import { sauvegarderProfil } from "../utils/profil";

const GV_PROFIL = {
  vert:"#2D7A2D", vertClair:"#00A651", vertLight:"#E8F5E9",
  vertDark:"#1B5E20", blanc:"#FFFFFF", gris:"#F8F9FA",
  grisMid:"#6B7280", dark:"#111827", border:"#E5E7EB",
  orange:"#E8510A",
};

function InputField({ label, champ, placeholder, type="text", requis, identite, erreurs, setIdentite }) {
  return (
    <div style={{ marginBottom:14 }}>
      <label style={{ fontSize:12, fontWeight:700, color:GV_PROFIL.dark, display:"block", marginBottom:5 }}>
        {label} {requis && <span style={{ color:"#EF4444" }}>*</span>}
      </label>
      <input type={type} value={identite[champ]}
        onChange={e => setIdentite(p => ({...p, [champ]: e.target.value}))}
        placeholder={placeholder}
        style={{
          width:"100%", padding:"10px 12px", fontSize:13, boxSizing:"border-box",
          border:`1.5px solid ${erreurs[champ] ? "#EF4444" : GV_PROFIL.border}`,
          borderRadius:8, outline:"none", background:GV_PROFIL.blanc,
          transition:"border 0.2s"
        }}
      />
      {erreurs[champ] && <div style={{ fontSize:11, color:"#EF4444", marginTop:3 }}>{erreurs[champ]}</div>}
    </div>
  );
}

function PopupProfil({ onTerminer, currentUser }) {
  const { t } = useTranslation();
  // Démarrage direct à l'étape 2 — l'identité est déjà connue via le compte admin
  const [etape, setEtape] = useState(2);
  const [identite, setIdentite] = useState({
    prenom: currentUser?.prenom || currentUser?.name?.split(" ")[0] || "",
    nom:    currentUser?.nom    || currentUser?.name?.split(" ")[1] || "",
    organisation: currentUser?.organisation || "",
    typeOrg: "bureau_etudes",
    adresse: "", email: currentUser?.email || "", tel: "",
  });
  const [estGeo5, setEstGeo5] = useState(null); // null | true | false
  const [modulesPos, setModulesPos] = useState([]); // ids des modules possédés
  const [versionGeo5, setVersionGeo5] = useState("");
  const [erreurs, setErreurs] = useState({});

  const GV = GV_PROFIL;

  const validerIdentite = () => {
    const e = {};
    if (!identite.prenom.trim()) e.prenom = "Requis";
    if (!identite.nom.trim()) e.nom = "Requis";
    if (!identite.organisation.trim()) e.organisation = "Requis";
    if (!identite.email.trim() || !identite.email.includes("@")) e.email = "Email invalide";
    setErreurs(e);
    return Object.keys(e).length === 0;
  };

  const toggleModule = (id) => {
    setModulesPos(prev =>
      prev.includes(id) ? prev.filter(m => m !== id) : [...prev, id]
    );
  };

  const terminer = () => {
    const profil = {
      ...identite,
      estUtilisateurGeo5: estGeo5,
      modulesGeo5: estGeo5 ? modulesPos : [],
      versionGeo5: estGeo5 ? versionGeo5 : null,
      dateInscription: new Date().toISOString(),
    };
    sauvegarderProfil(profil);
    onTerminer(profil);
  };

  return (
    <div style={{
      position:"fixed", inset:0, zIndex:9999,
      background:"rgba(0,0,0,0.65)", backdropFilter:"blur(4px)",
      display:"flex", alignItems:"center", justifyContent:"center", padding:16
    }}>
      <div style={{
        background:GV.blanc, borderRadius:20, width:"100%", maxWidth:560,
        maxHeight:"90vh", overflowY:"auto",
        boxShadow:"0 24px 64px rgba(0,0,0,0.3)",
      }}>
        {/* Header popup */}
        <div style={{
          background:`linear-gradient(135deg, ${GV.vertDark}, ${GV.vert})`,
          borderRadius:"20px 20px 0 0", padding:"24px 28px",
        }}>
          <div style={{ display:"flex", alignItems:"center", gap:14 }}>
            <img src={LOGO_CIVISOFT} alt="CiviSoft" style={{ height:36, objectFit:"contain", filter:"brightness(10)" }}/>
            <div>
              <div style={{ fontSize:17, fontWeight:900, color:GV.blanc }}>GEO5 Expert Advisor</div>
              <div style={{ fontSize:12, color:"rgba(255,255,255,0.75)", marginTop:2 }}>Civisoft France — Distributeur Exclusif GEO5</div>
            </div>
          </div>
          {/* Barre progression */}
          <div style={{ display:"flex", gap:8, marginTop:20 }}>
            {[
              { n:2, label:"Utilisez-vous GEO5 ?" },
              ...(estGeo5 ? [{ n:3, label:"Mes modules GEO5" }] : []),
            ].map(s => (
              <div key={s.n} style={{ flex:1 }}>
                <div style={{ fontSize:10, color: etape >= s.n ? GV.blanc : "rgba(255,255,255,0.4)", fontWeight:700, marginBottom:4 }}>
                  {s.label}
                </div>
                <div style={{
                  height:3, borderRadius:2,
                  background: etape > s.n ? GV.vertClair : etape === s.n ? GV.blanc : "rgba(255,255,255,0.25)"
                }}/>
              </div>
            ))}
          </div>
        </div>

        {/* Corps */}
        <div style={{ padding:"28px 28px 20px" }}>

          {/* ── ÉTAPE 1 : IDENTITÉ ── */}
          {etape === 1 && (
            <div>
              <h3 style={{ fontSize:16, fontWeight:800, color:GV.dark, margin:"0 0 4px" }}>Bienvenue !</h3>
              <p style={{ fontSize:13, color:GV.grisMid, margin:"0 0 20px" }}>
                Quelques informations pour personnaliser votre expérience.
              </p>

              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:0 }}>
                <div style={{ paddingRight:8 }}><InputField label="Prénom" champ="prenom" placeholder="Karim" requis identite={identite} erreurs={erreurs} setIdentite={setIdentite}/></div>
                <div style={{ paddingLeft:8 }}><InputField label="Nom" champ="nom" placeholder="Boudjakdji" requis identite={identite} erreurs={erreurs} setIdentite={setIdentite}/></div>
              </div>

              {/* Type organisation */}
              <div style={{ marginBottom:14 }}>
                <label style={{ fontSize:12, fontWeight:700, color:GV.dark, display:"block", marginBottom:8 }}>
                  Type d'organisation <span style={{ color:"#EF4444" }}>*</span>
                </label>
                <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                  {[
                    { val:"bureau_etudes", label:"Bureau d'études" },
                    { val:"laboratoire", label:"Laboratoire" },
                    { val:"entreprise", label:"Entreprise BTP" },
                    { val:"universite", label:"Université" },
                    { val:"administration", label:"Administration" },
                    { val:"particulier", label:"Particulier" },
                  ].map(t => (
                    <button key={t.val} onClick={() => setIdentite(p => ({...p, typeOrg: t.val}))}
                      style={{
                        padding:"7px 14px", borderRadius:99, fontSize:12, cursor:"pointer",
                        border:`1.5px solid ${identite.typeOrg === t.val ? GV.vert : GV.border}`,
                        background: identite.typeOrg === t.val ? GV.vertLight : GV.blanc,
                        color: identite.typeOrg === t.val ? GV.vertDark : GV.dark,
                        fontWeight: identite.typeOrg === t.val ? 700 : 400,
                      }}>{t.label}</button>
                  ))}
                </div>
              </div>

              <InputField label="Nom du laboratoire / bureau / entreprise" champ="organisation" placeholder="CiviSoft France" requis identite={identite} erreurs={erreurs} setIdentite={setIdentite}/>
              <InputField label="Email professionnel" champ="email" placeholder="email@exemple.com" type="email" requis identite={identite} erreurs={erreurs} setIdentite={setIdentite}/>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:0 }}>
                <div style={{ paddingRight:8 }}><InputField label="Téléphone" champ="tel" placeholder="+33 1 XX XX XX XX" identite={identite} erreurs={erreurs} setIdentite={setIdentite}/></div>
                <div style={{ paddingLeft:8 }}><InputField label="Ville / Département" champ="adresse" placeholder="Paris" identite={identite} erreurs={erreurs} setIdentite={setIdentite}/></div>
              </div>
            </div>
          )}

          {/* ── ÉTAPE 2 : GEO5 ? ── */}
          {etape === 2 && (
            <div>
              <h3 style={{ fontSize:16, fontWeight:800, color:GV.dark, margin:"0 0 4px" }}>
                {t("popup.bienvenue")}{identite.prenom ? `, ${identite.prenom}` : ""} !
              </h3>
              <p style={{ fontSize:13, color:GV.grisMid, margin:"0 0 24px" }}>
                {t("popup.une_question")}
              </p>

              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:20 }}>
                {[
                  { val:true, label: t("popup.geo5_oui"), icone:"✅", desc: t("popup.geo5_oui_desc") },
                  { val:false, label: t("popup.geo5_non"), icone:"❌", desc: t("popup.geo5_non_desc") },
                ].map(o => (
                  <button key={String(o.val)} onClick={() => setEstGeo5(o.val)}
                    style={{
                      padding:"20px 16px", borderRadius:12, cursor:"pointer", textAlign:"left",
                      border:`2px solid ${estGeo5 === o.val ? GV.vert : GV.border}`,
                      background: estGeo5 === o.val ? GV.vertLight : GV.blanc,
                      boxShadow: estGeo5 === o.val ? `0 0 0 3px ${GV.vert}25` : "none",
                      transition:"all 0.18s"
                    }}>
                    <div style={{ fontSize:28, marginBottom:10 }}>{o.icone}</div>
                    <div style={{ fontSize:13, fontWeight:700, color: estGeo5 === o.val ? GV.vertDark : GV.dark, marginBottom:4 }}>{o.label}</div>
                    <div style={{ fontSize:11, color:GV.grisMid }}>{o.desc}</div>
                  </button>
                ))}
              </div>

              {estGeo5 === false && (
                <div style={{ background:"#FFF7ED", border:"1px solid #FED7AA", borderRadius:10, padding:"14px 16px" }}>
                  <div style={{ fontSize:13, fontWeight:700, color:"#92400E", marginBottom:6 }}>💡 {t("popup.acquis_geo5")}</div>
                  <div style={{ fontSize:12, color:"#78350F", lineHeight:1.7 }}>
                    {t("popup.contact_geo5")}<br/>
                    Contactez-nous pour un devis personnalisé :<br/>
                    <strong>📞 {CIVISOFT_CONTACT.tel}</strong> · <strong>✉ {CIVISOFT_CONTACT.email}</strong>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── ÉTAPE 3 : MODULES GEO5 POSSÉDÉS ── */}
          {etape === 3 && (
            <div>
              <h3 style={{ fontSize:16, fontWeight:800, color:GV.dark, margin:"0 0 4px" }}>{t("popup.modules_titre")}</h3>
              <p style={{ fontSize:13, color:GV.grisMid, margin:"0 0 8px" }}>
                {t("popup.modules_hint")}
              </p>

              {/* Version */}
              <div style={{ marginBottom:16 }}>
                <label style={{ fontSize:12, fontWeight:700, color:GV.dark, display:"block", marginBottom:6 }}>Version GEO5</label>
                <select value={versionGeo5} onChange={e => setVersionGeo5(e.target.value)}
                  style={{ width:"100%", padding:"10px 12px", fontSize:13, border:`1.5px solid ${GV.border}`, borderRadius:8, background:GV.blanc, outline:"none" }}>
                  <option value="">Sélectionner la version...</option>
                  {VERSIONS_GEO5.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>

              {/* Tout sélectionner */}
              <div style={{ display:"flex", justifyContent:"space-between", marginBottom:12 }}>
                <span style={{ fontSize:12, color:GV.grisMid }}>{modulesPos.length} module(s) sélectionné(s)</span>
                <div style={{ display:"flex", gap:8 }}>
                  <button onClick={() => setModulesPos(GEO5_MODULES_LISTE.flatMap(c => c.modules.map(m => m.id)))}
                    style={{ fontSize:11, color:GV.vert, background:"transparent", border:"none", cursor:"pointer", fontWeight:700 }}>
                    Tout sélectionner
                  </button>
                  <button onClick={() => setModulesPos([])}
                    style={{ fontSize:11, color:GV.grisMid, background:"transparent", border:"none", cursor:"pointer" }}>
                    Tout désélectionner
                  </button>
                </div>
              </div>

              {/* Modules par catégorie */}
              <div style={{ maxHeight:280, overflowY:"auto", border:`1px solid ${GV.border}`, borderRadius:10, padding:"4px 0" }}>
                {GEO5_MODULES_LISTE.map(cat => (
                  <div key={cat.cat}>
                    <div style={{ padding:"8px 14px 4px", fontSize:10, fontWeight:800, color:GV.grisMid, textTransform:"uppercase", letterSpacing:1, background:GV.gris }}>
                      {cat.cat}
                    </div>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:0 }}>
                      {cat.modules.map(m => {
                        const sel = modulesPos.includes(m.id);
                        return (
                          <button key={m.id} onClick={() => toggleModule(m.id)}
                            style={{
                              display:"flex", alignItems:"center", gap:10, padding:"9px 14px",
                              background: sel ? GV.vertLight : "transparent",
                              border:"none", borderBottom:`1px solid ${GV.border}`,
                              cursor:"pointer", textAlign:"left", transition:"background 0.15s"
                            }}>
                            <div style={{
                              width:16, height:16, borderRadius:4, flexShrink:0,
                              border:`2px solid ${sel ? GV.vert : GV.border}`,
                              background: sel ? GV.vert : "transparent",
                              display:"flex", alignItems:"center", justifyContent:"center",
                            }}>
                              {sel && <span style={{ color:"#fff", fontSize:10, fontWeight:900 }}>✓</span>}
                            </div>
                            <span style={{ fontSize:11 }}>{m.icone}</span>
                            <span style={{ fontSize:12, fontWeight: sel ? 700 : 400, color: sel ? GV.vertDark : GV.dark }}>{m.nom}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div style={{ padding:"16px 28px 24px", borderTop:`1px solid ${GV.border}`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          {etape > 2 ? (
            <button onClick={() => setEtape(e => e-1)}
              style={{ background:"transparent", border:`1.5px solid ${GV.border}`, borderRadius:8, padding:"10px 20px", fontSize:13, cursor:"pointer", color:GV.dark, fontWeight:600 }}>
              {t("popup_profil.precedent")}
            </button>
          ) : <div/>}

          {etape === 1 && (
            <button onClick={() => { if(validerIdentite()) setEtape(2); }}
              style={{
                background:`linear-gradient(135deg, ${GV.vert}, ${GV.vertClair})`,
                border:"none", borderRadius:8, padding:"11px 28px", fontSize:13,
                fontWeight:800, cursor:"pointer", color:GV.blanc,
                boxShadow:`0 4px 14px ${GV.vert}40`
              }}>
              Suivant →
            </button>
          )}
          {etape === 2 && (
            <button
              disabled={estGeo5 === null}
              onClick={() => estGeo5 ? setEtape(3) : terminer()}
              style={{
                background: estGeo5 !== null ? `linear-gradient(135deg, ${GV.vert}, ${GV.vertClair})` : GV.border,
                border:"none", borderRadius:8, padding:"11px 28px", fontSize:13,
                fontWeight:800, cursor: estGeo5 !== null ? "pointer" : "not-allowed",
                color: estGeo5 !== null ? GV.blanc : GV.grisMid,
              }}>
              {estGeo5 ? t("popup.btn_suivant") : estGeo5 === false ? t("popup.btn_terminer") : "—"}
            </button>
          )}
          {etape === 3 && (
            <button onClick={terminer}
              style={{
                background:`linear-gradient(135deg, ${GV.vert}, ${GV.vertClair})`,
                border:"none", borderRadius:8, padding:"11px 28px", fontSize:13,
                fontWeight:800, cursor:"pointer", color:GV.blanc,
                boxShadow:`0 4px 14px ${GV.vert}40`
              }}>
              {t("popup.btn_terminer")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// BADGE ALERTE MODULES MANQUANTS — affiché dans PageSolution
// =============================================================================

export default PopupProfil;
